import { DPR } from '@constants/common.const';
import { Collectible } from '@models/collectibles';
import { TranslationService } from '@services/core/TranslationService';
import { ShadowNineSlice } from './Shadow9Slice';
import { GameConfigService } from '@services/core/GameConfigService';

type HeaderBarOptions = {
  scene: Phaser.Scene;
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  onBack: () => void;
  /** Optional initial collectibles to render. */
  collectibles?: Collectible[];
};

export class HeaderBar extends Phaser.GameObjects.Container {
  private isPanelOpen = false;
  private opts: HeaderBarOptions;
  private cfg = new GameConfigService();

  private headerContainer!: Phaser.GameObjects.Container;
  private dropdownPanel!: Phaser.GameObjects.Container;

  private collectibles: Collectible[] = [];
  private textColor: string;
  private pillIcons = new Map<string, Phaser.GameObjects.Image>();

  constructor(options: HeaderBarOptions) {
    super(options.scene, options.x, options.y);
    this.opts = options;
    this.collectibles = options.collectibles ?? [];
    this.textColor = this.cfg.getColorHex('--color-neutral-100')!;
    options.scene.add.existing(this);

    // Initial build
    this.build(options.width, options.height);

    // FORCE CLOSED ON INIT
    this.closePanelInstant();
  }

  /** Push new collectibles and re-render the entire bar. */
  public updateCollectibles(
    collectibles: Collectible[],
    claimedCollectibleUuid?: string,
  ) {
    this.collectibles = collectibles ?? [];
    this.rebuild(this.opts.width, this.opts.height);
    // animate only on claiming the collectible
    if (claimedCollectibleUuid) {
      this.animateClaimedIcon(claimedCollectibleUuid);
    }
  }

  private animateClaimedIcon(uuid: string) {
    const icon = this.pillIcons.get(uuid);
    if (!icon) return;

    this.scene.tweens.add({
      targets: icon,
      scale: 1.1,
      duration: 500,
      ease: 'Cubic.easeOut',
      yoyo: true,
    });
  }

  public rebuild(width: number, height: number) {
    const wasOpen = this.isPanelOpen;
    // Store reference to prevent logic jumping during rebuild
    this.removeAll(true);

    this.build(width, height);

    if (wasOpen) {
      this.openPanelInstant();
    } else {
      this.closePanelInstant();
    }
  }

  private build(width: number, height: number) {
    this.buildPill(width, height);
    this.buildPanel(width);
  }

  private buildPill(width: number, height: number) {
    const pillW = width;
    const pillH = height;

    this.pillIcons.clear();
    this.headerContainer = this.scene.add.container(0, 0);

    const shadow = new ShadowNineSlice(
      this.scene,
      0,
      height / 2,
      width,
      height,
      'rounded',
    );

    // Bake white pill background into RT — no earcut every frame
    const pillBgRT = this.scene.add.renderTexture(-pillW / 2, 0, pillW, pillH);
    pillBgRT.setOrigin(0, 0);
    const pillGfx = this.scene.add.graphics();
    pillGfx.fillStyle(this.cfg.getColor('--color-base-1')!, 0.9);
    pillGfx.fillRoundedRect(0, 0, pillW, pillH, this.opts.radius);
    pillBgRT.draw(pillGfx, 0, 0);
    pillGfx.destroy();

    const backArrow = this.scene.add
      .image(-pillW / 2 + 18 * DPR, pillH / 2, 'arrow-left')
      .setOrigin(0, 0.5)
      .setDisplaySize(22 * DPR, 22 * DPR)
      .setInteractive({ useHandCursor: true });

    backArrow.on('pointerdown', () => this.opts.onBack?.());

    const chevron = this.scene.add
      .image(pillW / 2 - 18 * DPR, pillH / 2, 'arrow-down')
      .setOrigin(1, 0.5)
      .setDisplaySize(22 * DPR, 22 * DPR);

    const centerRow = this.scene.add.container(0, pillH / 2);

    // --- Dynamic Coordinate Calculation ---
    const groupGap = 80 * DPR;
    const totalItems = this.collectibles.length;
    // Calculate start X to center the group
    const startX = -((totalItems - 1) * groupGap) / 2;

    const iconSize = 22 * DPR;

    const makeStat = (x: number, item: Collectible) => {
      const c = this.scene.add.container(x, 0);
      const textureKey = this.scene.textures.exists(item.uuid)
        ? item.uuid
        : '__DEFAULT';
      const icon = this.scene.add
        .image(-18 * DPR, 0, textureKey)
        .setDisplaySize(iconSize, iconSize)
        .setOrigin(0.5);
      this.pillIcons.set(item.uuid, icon);
      const label = this.scene.add
        .text(2 * DPR, 0, `${item.value}/${item.targetCount}`, {
          fontFamily: 'korolev, sans-serif',
          fontSize: `${12 * DPR}px`,
          color: this.textColor,
          fontStyle: '500',
        })
        .setOrigin(0, 0.5);

      c.add([icon, label]);
      return c;
    };

    const stats = this.collectibles.map((c, i) =>
      makeStat(startX + i * groupGap, c),
    );

    centerRow.add(stats);

    const chevronHit = this.scene.add
      .zone(pillW / 2 - 32 * DPR, pillH / 2, 56 * DPR, pillH)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    chevronHit.on('pointerdown', () => this.toggle());

    this.headerContainer.add([
      shadow,
      pillBgRT,
      backArrow,
      centerRow,
      chevron,
      chevronHit,
    ]);
    this.add(this.headerContainer);
  }

  private buildPanel(width: number) {
    const panelW = width;
    const panelH = 240 * DPR;
    const panelRadius = this.opts.radius;

    this.dropdownPanel = this.scene.add.container(0, 0);

    const shadow = new ShadowNineSlice(
      this.scene,
      0,
      panelH / 2,
      width,
      panelH,
      'rounded',
    );

    const headerH = 48 * DPR;
    const rowH = 44 * DPR;
    const rowGap = 14 * DPR;

    const rowYs = this.collectibles.map(
      (_, i) => headerH + 12 * DPR + (rowH + rowGap) * i,
    );

    // Bake white panel bg + all row backgrounds into a single RT — no earcut per frame
    const panelBgRT = this.scene.add.renderTexture(
      -panelW / 2,
      0,
      panelW,
      panelH,
    );
    panelBgRT.setOrigin(0, 0);
    const panelGfx = this.scene.add.graphics();
    panelGfx.fillStyle(this.cfg.getColor('--color-base-1')!, 1);
    panelGfx.fillRoundedRect(0, 0, panelW, panelH, panelRadius);
    panelGfx.fillStyle(this.cfg.getColor('--color-neutral-100')!, 0.05);
    rowYs.forEach((y) => {
      panelGfx.fillRoundedRect(14 * DPR, y, panelW - 28 * DPR, rowH, 12 * DPR);
    });
    panelBgRT.draw(panelGfx, 0, 0);
    panelGfx.destroy();

    const t = new TranslationService();
    const title = this.scene.add
      .text(
        -panelW / 2 + 18 * DPR,
        16 * DPR,
        t.translate('header.collectibles'),
        {
          fontFamily: 'korolev, sans-serif',
          fontSize: `${16 * DPR}px`,
          color: this.textColor,
          fontStyle: '500',
        },
      )
      .setOrigin(0, 0);

    const chevronUp = this.scene.add
      .image(panelW / 2 - 18 * DPR, 16 * DPR, 'arrow-up')
      .setOrigin(1, 0)
      .setDisplaySize(20 * DPR, 20 * DPR);

    const headerHit = this.scene.add
      .zone(0, 24 * DPR, panelW, 48 * DPR)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    headerHit.on('pointerdown', () => this.toggle());

    this.dropdownPanel.add([shadow, panelBgRT, title, chevronUp, headerHit]);

    this.collectibles.forEach((c, i) => {
      this.addRowIcons(rowYs[i], c, panelW, rowH);
    });

    this.add(this.dropdownPanel);
  }

  private addRowIcons(
    rowY: number,
    collectible: Collectible,
    panelW: number,
    rowH: number,
  ) {
    const startX = -panelW / 2 + 46 * DPR;
    const iconGap = 30 * DPR;
    const size = 22 * DPR;
    const y = rowY + rowH / 2;

    // Icons (Earned vs Grayed out)
    for (let i = 0; i < collectible.targetCount; i++) {
      const img = this.scene.add
        .image(startX + i * iconGap, y, collectible.uuid)
        .setDisplaySize(size, size)
        .setOrigin(0.5);

      if (i >= collectible.value) {
        const fx = img.preFX?.addColorMatrix();
        if (fx) fx.blackWhite(true);
      }
      this.dropdownPanel.add(img);
    }

    // Max label width = gap between the right edge of the last icon and the
    // label's right anchor, minus an 8px gap so text never touches the icons.
    const lastIconRight = startX + (collectible.targetCount - 1) * iconGap + size / 2;
    const labelX = panelW / 2 - 32 * DPR;
    const iconTextGap = 8 * DPR;
    const maxLabelWidth = labelX - lastIconRight - iconTextGap;

    // Row Label (Right Aligned, word-wrapped to available space)
    const label = this.scene.add
      .text(labelX, y, collectible.prize.label, {
        fontFamily: 'korolev, sans-serif',
        fontSize: `${12 * DPR}px`,
        color: this.textColor,
        fontStyle: '500',
        align: 'right',
        wordWrap: { width: maxLabelWidth },
      })
      .setOrigin(1, 0.5);

    this.dropdownPanel.add(label);
  }

  private toggle() {
    if (this.isPanelOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  private openPanelInstant() {
    this.isPanelOpen = true;
    this.headerContainer.setVisible(false);
    this.dropdownPanel.setVisible(true).setAlpha(1);
  }

  private closePanelInstant() {
    this.isPanelOpen = false;
    this.dropdownPanel.setVisible(false).setAlpha(0);
    this.headerContainer.setVisible(true);
  }

  private openPanel() {
    this.isPanelOpen = true;
    this.dropdownPanel.setVisible(true).setAlpha(0);
    this.headerContainer.setVisible(false);

    this.scene.tweens.add({
      targets: this.dropdownPanel,
      alpha: 1,
      duration: 180,
      ease: 'Cubic.easeOut',
    });
  }

  private closePanel() {
    this.isPanelOpen = false;
    this.scene.tweens.killTweensOf(this.dropdownPanel);
    this.dropdownPanel.setVisible(false);
    this.headerContainer.setVisible(true);

    this.scene.tweens.add({
      targets: this.dropdownPanel,
      alpha: 0,
      duration: 150,
      ease: 'Cubic.easeIn',
    });
  }
}
